import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TimelineEntryType } from '~/constants/timelineConstants'
import {
  setTimelineItem,
  updateTimelineClientData,
  updateTimelineFilters,
} from '~/store/actions/timeline'
import {
  fetchPrescription,
  getPrescriptionById,
  getPrescriptionError,
  getPrescriptionIsLoading,
} from '~/store/duck/prescriptions'
import {
  getTimelineIsLoading,
  getTimelineItem,
  getTimelineList,
} from '~/store/reducers/timeline'
import type { PrescriptionTimelineItem } from '~/types'

/**
 * Hook for loading prescription timeline items with instant caching.
 * Checks: Redux cache → Timeline list → API fetch
 */
export function useRxTimelineItem(prescriptionId?: string) {
  const dispatch = useDispatch()
  const fetchStartedRef = useRef<string | undefined>()

  const cachedTimelineItem = useSelector(
    getTimelineItem,
  ) as PrescriptionTimelineItem | null
  const timelineList = useSelector(getTimelineList)
  const prescription = useSelector(
    prescriptionId ? getPrescriptionById(prescriptionId) : () => undefined,
  )
  const isPrescriptionLoading = useSelector(getPrescriptionIsLoading)
  const isTimelineLoading = useSelector(getTimelineIsLoading)
  const prescriptionError = useSelector(getPrescriptionError)

  const timelineItem = useMemo(() => {
    if (!prescriptionId) return undefined

    // Check cache first
    if (cachedTimelineItem?.prescription?.id === prescriptionId) {
      return cachedTimelineItem
    }

    // Check timeline list
    return timelineList.find(
      (item) =>
        item?.type === TimelineEntryType.PRESCRIPTION &&
        (item as PrescriptionTimelineItem).prescription?.id === prescriptionId,
    ) as PrescriptionTimelineItem | undefined
  }, [prescriptionId, cachedTimelineItem, timelineList])

  const source = timelineItem?.prescription ?? prescription
  const clientId = source?.client?.id ?? source?.clientId ?? ''
  const patientId = source?.patient?.id ?? source?.patientId ?? ''

  useEffect(() => {
    if (timelineItem) return
    if (!prescriptionId) return
    if (prescription?.id === prescriptionId) return
    if (isPrescriptionLoading) return
    // Only block if error is for THIS prescription
    if (prescriptionError && prescription === undefined) return

    dispatch(fetchPrescription(prescriptionId))
  }, [
    prescriptionId,
    timelineItem,
    prescription?.id,
    prescription,
    isPrescriptionLoading,
    prescriptionError,
    dispatch,
  ])

  useEffect(() => {
    if (timelineItem) return
    if (!prescriptionId) return
    if (!prescription) return
    if (fetchStartedRef.current === prescriptionId) return

    const prescriptionClientId =
      prescription.client?.id ?? prescription.clientId
    const prescriptionPatientId =
      prescription.patient?.id ?? prescription.patientId

    if (!prescriptionClientId || !prescriptionPatientId) return

    fetchStartedRef.current = prescriptionId
    dispatch(
      updateTimelineClientData({
        clientId: prescriptionClientId,
        patientId: prescriptionPatientId,
        from: 0,
        to: 100,
      }),
    )
    dispatch(updateTimelineFilters([TimelineEntryType.PRESCRIPTION]))
  }, [timelineItem, prescriptionId, prescription, dispatch])

  // Cache item when timeline fetch completes
  useEffect(() => {
    if (!timelineItem || !prescriptionId) return
    if (cachedTimelineItem?.prescription?.id === prescriptionId) return

    dispatch(setTimelineItem(timelineItem))
  }, [timelineItem, prescriptionId, cachedTimelineItem, dispatch])

  // Clear fetchStartedRef when timeline item is found or error occurs
  useEffect(() => {
    if (!prescriptionId) return
    if (timelineItem || prescriptionError) {
      fetchStartedRef.current = undefined
    }
  }, [prescriptionId, timelineItem, prescriptionError])

  const loading =
    Boolean(prescriptionId) &&
    !prescriptionError &&
    !timelineItem &&
    (isPrescriptionLoading ||
      isTimelineLoading ||
      (!prescription && fetchStartedRef.current !== prescriptionId))

  return {
    timelineItem,
    prescription,
    clientId,
    patientId,
    loading,
    error: prescriptionError,
  }
}
